import { i18n } from '@/tool/i18n'

const isProd = window.location.hostname === 'www.piroker.com';
const BASE_URL = isProd
    ? 'https://www.piroker.com/gameResources/wiki/'
    : 'https://www.test.piroker.com/gameResources/wiki/';
const ICON_BASE_URL = BASE_URL + 'icon/';

// 支持的语言列表
const LANG_OPTIONS = [
    { key: 'zh', label: '简体中文' },
    { key: 'zh-TW', label: '繁體中文' },
    { key: 'en', label: 'English' },
    { key: 'ja', label: '日本語' },
    { key: 'ko', label: '한국어' },
    { key: 'fr', label: 'Français' },
    { key: 'de', label: 'Deutsch' },
    { key: 'es', label: 'Español' },
    { key: 'pt', label: 'Português' },
    { key: 'ru', label: 'Русский' },
    { key: 'ar', label: 'العربية' },
    { key: 'th', label: 'ไทย' },
    { key: 'vi', label: 'Tiếng Việt' },
    { key: 'id', label: 'Bahasa Indonesia' },
    { key: 'tr', label: 'Türkçe' },
    { key: 'it', label: 'Italiano' },
];

// 稀有度配置
const RARITY_CONFIG = {
    treasures: {
        1: { i18nKey: 'common', color: '#A2D17C' },
        2: { i18nKey: 'rare', color: '#77A3F1' },
        3: { i18nKey: 'epic', color: '#C392FA' },
        4: { i18nKey: 'legendary', color: '#F8C040' },
    },
    fishes: {
        1: { i18nKey: 'common', color: '#A2D17C' },
        2: { i18nKey: 'rare', color: '#77A3F1' },
        3: { i18nKey: 'epic', color: '#C392FA' },
        4: { i18nKey: 'legendary', color: '#F8C040' },
    }
};

// 分类配置
const CATEGORIES = [
    { key: 'treasures', i18nKey: 'relic', icon: 'icon_gift' },
    { key: 'fishes', i18nKey: 'fish', icon: 'icon_fish' },
    { key: 'heroes', i18nKey: 'hero', icon: 'icon_hero' },
];

async function loadLangData(category, lang) {
    const url = `${BASE_URL}${category}/${lang}.json`;
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        return await res.json();
    } catch (e) {
        if (lang !== 'en') {
            return loadLangData(category, 'en');
        }
        return [];
    }
}

export default {
    props: {
        parentLang: { type: String, default: '' }
    },
    data() {
        return {
            ICON_BASE_URL,
            RARITY_CONFIG,
            CATEGORIES,
            LANG_OPTIONS,
            lang: 'en',
            activeCategory: 'treasures',
            expandedCategories: { treasures: true, fishes: false, heroes: false },
            selectedRarities: [],
            selectedTypes: [],
            treasureTypes: [],
            showLangPicker: false,
            showDetail: false,
            detailIndex: -1,
            sortAsc: true,
            allData: {
                treasures: [],
                fishes: [],
                heroes: [],
            }
        }
    },
    watch: {
        parentLang(newVal) {
            if (newVal) {
                this.lang = newVal;
                this.loadAllData();
                this.loadTreasureTypes();
            }
        }
    },
    mounted() {
        if (this.parentLang) {
            this.lang = this.parentLang;
        } else {
            this.detectLanguage();
        }
        this.loadAllData();
        this.loadTreasureTypes();
    },
    computed: {
        currentItems() {
            let items = [...(this.allData[this.activeCategory] || [])];
            if (this.selectedRarities.length > 0) {
                items = items.filter(item => this.selectedRarities.includes(item.rarity));
            }
            if (this.activeCategory === 'treasures' && this.selectedTypes.length > 0) {
                items = items.filter(item => this.selectedTypes.includes(item.type));
            }
            items.sort((a, b) => this.sortAsc ? a.rarity - b.rarity : b.rarity - a.rarity);
            return items;
        },
        currentRarityConfig() {
            return RARITY_CONFIG[this.activeCategory] || null;
        },
        detailItem() {
            if (this.detailIndex < 0 || !this.currentItems.length) return null;
            return this.currentItems[this.detailIndex] || null;
        },
        t() {
            return i18n[this.lang] || i18n.en;
        }
    },
    methods: {
        detectLanguage() {
            const browserLang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
            const langMap = {
                'zh-cn': 'zh', 'zh-sg': 'zh', 'zh-tw': 'zh-TW', 'zh-hk': 'zh-TW', 'zh': 'zh',
                'ja': 'ja', 'ko': 'ko', 'fr': 'fr', 'de': 'de', 'es': 'es',
                'pt': 'pt', 'ru': 'ru', 'ar': 'ar', 'th': 'th', 'vi': 'vi',
                'id': 'id', 'tr': 'tr', 'it': 'it', 'en': 'en',
            };
            this.lang = langMap[browserLang] || langMap[browserLang.split('-')[0]] || 'en';
        },
        async loadTreasureTypes() {
            try {
                const url = `${BASE_URL}treasureTypes/${this.lang}.json`;
                const res = await fetch(url);
                if (!res.ok) throw new Error(res.status);
                this.treasureTypes = await res.json();
            } catch (e) {
                if (this.lang !== 'en') {
                    try {
                        const res = await fetch(`${BASE_URL}treasureTypes/en.json`);
                        if (res.ok) this.treasureTypes = await res.json();
                    } catch (_) {}
                }
            }
        },
        toggleType(typeId) {
            const idx = this.selectedTypes.indexOf(typeId);
            if (idx > -1) {
                this.selectedTypes.splice(idx, 1);
            } else {
                this.selectedTypes.push(typeId);
            }
        },
        async loadAllData() {
            const results = await Promise.all(
                CATEGORIES.map(cat => loadLangData(cat.key, this.lang))
            );
            CATEGORIES.forEach((cat, i) => {
                this.allData[cat.key] = results[i];
            });
        },
        toggleCategory(key) {
            if (this.activeCategory === key) {
                this.expandedCategories[key] = !this.expandedCategories[key];
            } else {
                Object.keys(this.expandedCategories).forEach(k => {
                    this.expandedCategories[k] = false;
                });
                this.expandedCategories[key] = true;
                this.activeCategory = key;
                this.selectedRarities = [];
                this.selectedTypes = [];
                this.sortAsc = true;
            }
        },
        toggleRarity(rarity) {
            const idx = this.selectedRarities.indexOf(rarity);
            if (idx > -1) {
                this.selectedRarities.splice(idx, 1);
            } else {
                this.selectedRarities.push(rarity);
            }
        },
        getIconUrl(icon) {
            return ICON_BASE_URL + icon;
        },
        getRarityBgStyle(item) {
            const prefixMap = { treasures: 't', fishes: 'f' };
            const prefix = prefixMap[this.activeCategory];
            let bgUrl;
            if (prefix && item.rarity) {
                const suffix = (prefix === 't' && item.mergeLevel === 2) ? '_active' : '';
                bgUrl = `../../../static/guide/pc/${prefix}_bg_${item.rarity}${suffix}.png`;
            }
            if (bgUrl) {
                return {
                    backgroundImage: `url('${bgUrl}')`,
                    backgroundRepeat: 'no-repeat',
                };
            }
            return { backgroundColor: '#3d3867' };
        },
        getCategoryLabel(cat) {
            return this.t[cat.i18nKey] || cat.key;
        },
        getRarityLabel(rarity) {
            const config = RARITY_CONFIG[this.activeCategory];
            if (!config || !config[rarity]) return '';
            return this.t[config[rarity].i18nKey] || '';
        },
        formatDesc(desc) {
            if (!desc) return '';
            let html = desc;
            // [img]11[/img] → icon图片
            html = html.replace(/\[img\](\d+)\[\/img\]/g, (_, id) => {
                return `<img class="desc_icon" src="${ICON_BASE_URL}${id}.png" />`;
            });
            // <#HEXCOLOR>text</color> → 彩色文字
            html = html.replace(/<#([A-Fa-f0-9]{3,8})>(.*?)<\/color>/g, (_, color, text) => {
                return `<span style="color:#${color}">${text}</span>`;
            });
            // \n → 换行
            html = html.replace(/\\n/g, '<br>');
            return html;
        },
        toggleSort() {
            this.sortAsc = !this.sortAsc;
        },
        openDetail(index) {
            this.detailIndex = index;
            this.showDetail = true;
        },
        closeDetail() {
            this.showDetail = false;
            this.detailIndex = -1;
        },
        prevDetail() {
            if (this.detailIndex > 0) {
                this.detailIndex--;
            } else {
                this.detailIndex = this.currentItems.length - 1;
            }
        },
        nextDetail() {
            if (this.detailIndex < this.currentItems.length - 1) {
                this.detailIndex++;
            } else {
                this.detailIndex = 0;
            }
        },
        getDetailRarityLabel(item) {
            const config = RARITY_CONFIG[this.activeCategory];
            if (!config || !config[item.rarity]) return '';
            return this.t[config[item.rarity].i18nKey] || '';
        },
        currentLangLabel() {
            const opt = LANG_OPTIONS.find(l => l.key === this.lang);
            return opt ? opt.label : this.lang;
        },
        async switchLang(langKey) {
            this.lang = langKey;
            this.showLangPicker = false;
            await this.loadAllData();
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/guide/logic.js